import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Phone, CreditCard, Lock, Apple } from "lucide-react";
import { PHONE, PHONE_HREF } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (c) => `$${(c / 100).toFixed(2)}`;

let sdkPromise = null;
const loadSquareSdk = (env) => {
  if (window.Square) return Promise.resolve();
  if (sdkPromise) return sdkPromise;
  const url = env === "sandbox" ? "https://sandbox.web.squarecdn.com/v1/square.js" : "https://web.squarecdn.com/v1/square.js";
  sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = url; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
  return sdkPromise;
};

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-[#0a1128] placeholder:text-slate-400 focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20 outline-none transition-[border-color,box-shadow]";

/**
 * fields: [{ key, label, type?('select'|'checkbox'), required?, options?:[{value,label}] }]
 * category: 'passport'|'visa'  service: slug
 */
export const SquareCheckout = ({ title, category, service, fields = [], defaults = {} }) => {
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState(defaults);
  const [cust, setCust] = useState({ full_name: "", email: "", passport_number: "", company_name: "", departure_date: "", needed_date: "", notes: "" });
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [sqReady, setSqReady] = useState(false);
  const [wallets, setWallets] = useState({ apple: false, google: false, cashapp: false });
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);
  const appleRef = useRef(null);
  const googleRef = useRef(null);
  const cashRef = useRef(null);
  const gpayCleanup = useRef(null);

  const requiredSel = fields.filter((f) => f.required).map((f) => f.key);
  const selReady = requiredSel.every((k) => sel[k]);

  // Refs so wallet event listeners always read fresh values
  const custRef = useRef(cust); custRef.current = cust;
  const selRef = useRef(sel); selRef.current = sel;
  const selReadyRef = useRef(selReady); selReadyRef.current = selReady;

  useEffect(() => { axios.get(`${API}/square-config`).then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, []);

  // Live price
  useEffect(() => {
    if (!selReady) { setPrice(null); return; }
    axios.post(`${API}/quote-price`, { category, service, selections: sel })
      .then((r) => setPrice(r.data)).catch(() => setPrice(null));
  }, [JSON.stringify(sel), selReady, category, service]);

  const validate = useCallback(() => {
    const c = custRef.current;
    if (!c.full_name || !c.email) { toast.error("Please enter your name and email first."); return false; }
    if (!selReadyRef.current) { toast.error("Please complete the service options."); return false; }
    return true;
  }, []);

  const finalizeOrder = useCallback(async (token) => {
    const c = custRef.current; const s = selRef.current;
    await axios.post(`${API}/orders`, { category, service, selections: s, source_id: token, ...c, source_page: `order-${category}-${service}` });
    setDone(true);
    toast.success("Payment successful! Confirmation email on its way.");
  }, [category, service]);

  // Init Square payments + card when enabled
  useEffect(() => {
    if (!cfg?.enabled) return;
    let card;
    loadSquareSdk(cfg.environment).then(async () => {
      const payments = window.Square.payments(cfg.applicationId, cfg.locationId);
      paymentsRef.current = payments;
      setSqReady(true);
      card = await payments.card();
      await card.attach("#sq-card");
      cardRef.current = card;
    }).catch(() => {});
    return () => { try { card && card.destroy(); } catch (e) {} };
  }, [cfg?.enabled]);

  // (Re)build digital wallets whenever the authoritative total changes
  useEffect(() => {
    const payments = paymentsRef.current;
    if (!sqReady || !payments || !price?.total_cents) return;
    let cancelled = false;
    let req;
    try {
      req = payments.paymentRequest({
        countryCode: "US", currencyCode: "USD",
        total: { amount: (price.total_cents / 100).toFixed(2), label: "UPV Services" },
      });
    } catch (e) { return; }

    (async () => {
      // Apple Pay (Safari + registered domain only)
      try {
        const ap = await payments.applePay(req);
        if (!cancelled) { appleRef.current = ap; setWallets((w) => ({ ...w, apple: true })); }
      } catch (e) { setWallets((w) => ({ ...w, apple: false })); }

      // Google Pay
      try {
        const gp = await payments.googlePay(req);
        if (cancelled) return;
        googleRef.current = gp;
        await gp.attach("#gpay-btn");
        const el = document.getElementById("gpay-btn");
        const handler = async (e) => {
          e.preventDefault();
          if (!validate()) return;
          try {
            const r = await gp.tokenize();
            if (r.status !== "OK") return toast.error("Google Pay was cancelled.");
            setLoading(true);
            await finalizeOrder(r.token);
          } catch (err) { toast.error(err?.response?.data?.detail || "Google Pay payment failed."); }
          finally { setLoading(false); }
        };
        el && el.addEventListener("click", handler);
        gpayCleanup.current = () => el && el.removeEventListener("click", handler);
        setWallets((w) => ({ ...w, google: true }));
      } catch (e) { setWallets((w) => ({ ...w, google: false })); }

      // Cash App Pay (US)
      try {
        const cap = await payments.cashAppPay(req, { redirectURL: window.location.href, referenceId: `upv-${Date.now()}` });
        if (cancelled) return;
        cashRef.current = cap;
        cap.addEventListener("ontokenization", async (event) => {
          const { tokenResult, error } = event.detail || {};
          if (error || tokenResult?.status !== "OK") return toast.error("Cash App Pay was cancelled.");
          if (!validate()) return;
          try { setLoading(true); await finalizeOrder(tokenResult.token); }
          catch (err) { toast.error(err?.response?.data?.detail || "Payment failed."); }
          finally { setLoading(false); }
        });
        await cap.attach("#cashapp-btn");
        setWallets((w) => ({ ...w, cashapp: true }));
      } catch (e) { setWallets((w) => ({ ...w, cashapp: false })); }
    })();

    return () => {
      cancelled = true;
      try { gpayCleanup.current && gpayCleanup.current(); } catch (e) {}
      try { appleRef.current?.destroy?.(); } catch (e) {}
      try { googleRef.current?.destroy?.(); } catch (e) {}
      try { cashRef.current?.destroy?.(); } catch (e) {}
      appleRef.current = googleRef.current = cashRef.current = null;
      ["#gpay-btn", "#cashapp-btn"].forEach((id) => { const e = document.querySelector(id); if (e) e.innerHTML = ""; });
    };
  }, [sqReady, price?.total_cents, validate, finalizeOrder]);

  const setS = (k, v) => setSel((s) => ({ ...s, [k]: v }));
  const setC = (k) => (e) => setCust((c) => ({ ...c, [k]: e.target.value }));

  const pay = async () => {
    if (!validate()) return;
    if (!cardRef.current) return toast.error("Payment form is still loading. Please wait a moment.");
    setLoading(true);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== "OK") { setLoading(false); return toast.error("Please check your card details."); }
      await finalizeOrder(result.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed. Please try again or call us.");
    } finally { setLoading(false); }
  };

  const payApple = async (e) => {
    e.preventDefault();
    if (!appleRef.current) return;
    if (!validate()) return;
    try {
      const r = await appleRef.current.tokenize();
      if (r.status !== "OK") return toast.error("Apple Pay was cancelled.");
      setLoading(true);
      await finalizeOrder(r.token);
    } catch (err) { toast.error(err?.response?.data?.detail || "Apple Pay payment failed."); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(11,25,44,0.10)] border border-black/5 p-8 text-center" data-testid="order-success">
      <div className="mx-auto w-14 h-14 rounded-full bg-[#d90429]/10 flex items-center justify-center mb-5"><ShieldCheck className="w-7 h-7 text-[#d90429]" /></div>
      <h3 className="font-display font-bold text-2xl text-[#0b192c]">Order confirmed!</h3>
      <p className="text-slate-600 mt-3">Your payment went through and a confirmation email is on its way. A UPV specialist will be in touch shortly.</p>
      <a href={PHONE_HREF} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-6 py-3 font-semibold hover:bg-[#ef233c] transition-colors"><Phone className="w-4 h-4" /> Call {PHONE}</a>
    </div>
  );

  const anyWallet = wallets.apple || wallets.google || wallets.cashapp;

  return (
    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(11,25,44,0.10)] border border-black/5 p-6 md:p-7" data-testid="square-checkout">
      <div className="mb-5">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">Order Service</span>
        <h3 className="font-display font-bold text-2xl text-[#0b192c] mt-2">{title}</h3>
      </div>

      {/* Service option selectors */}
      <div className="space-y-4">
        {fields.map((f) => (
          f.type === "checkbox" ? (
            <label key={f.key} htmlFor={`opt-${f.key}`} className="flex items-center gap-3 cursor-pointer">
              <input id={`opt-${f.key}`} type="checkbox" checked={!!sel[f.key]} onChange={(e) => setS(f.key, e.target.checked)} className="w-4 h-4 accent-[#d90429]" data-testid={`opt-${f.key}`} />
              <span className="text-[15px] text-[#0b192c]">{f.label}</span>
            </label>
          ) : (
            <div key={f.key}>
              <label htmlFor={`opt-${f.key}`} className="block text-sm font-medium text-[#0b192c] mb-1.5">{f.label}{f.required && <span className="text-[#b00020]"> *<span className="sr-only"> required</span></span>}</label>
              <select id={`opt-${f.key}`} aria-required={f.required || undefined} className={inputCls} value={sel[f.key] || ""} onChange={(e) => setS(f.key, e.target.value)} data-testid={`opt-${f.key}`}>
                <option value="">Please choose…</option>
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )
        ))}
      </div>

      {/* Customer + travel */}
      <div className="mt-4 space-y-4">
        <div><label htmlFor="co-name" className="block text-sm font-medium text-[#0b192c] mb-1.5">Full Name <span className="text-[#b00020]">*<span className="sr-only"> required</span></span></label><input id="co-name" autoComplete="name" aria-required="true" className={inputCls} value={cust.full_name} onChange={setC("full_name")} data-testid="co-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label htmlFor="co-email" className="block text-sm font-medium text-[#0b192c] mb-1.5">Email <span className="text-[#b00020]">*<span className="sr-only"> required</span></span></label><input id="co-email" type="email" autoComplete="email" aria-required="true" className={inputCls} value={cust.email} onChange={setC("email")} data-testid="co-email" /></div>
          <div><label htmlFor="co-passport" className="block text-sm font-medium text-[#0b192c] mb-1.5">Passport #</label><input id="co-passport" className={inputCls} value={cust.passport_number} onChange={setC("passport_number")} placeholder="Optional" data-testid="co-passport" /></div>
        </div>
        <div><label htmlFor="co-company" className="block text-sm font-medium text-[#0b192c] mb-1.5">Company</label><input id="co-company" autoComplete="organization" className={inputCls} value={cust.company_name} onChange={setC("company_name")} placeholder="Optional" data-testid="co-company" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label htmlFor="co-departure" className="block text-sm font-medium text-[#0b192c] mb-1.5">Departure Date</label><input id="co-departure" type="date" className={inputCls} value={cust.departure_date} onChange={setC("departure_date")} data-testid="co-departure" /></div>
          <div><label htmlFor="co-needed" className="block text-sm font-medium text-[#0b192c] mb-1.5">Needed Date</label><input id="co-needed" type="date" className={inputCls} value={cust.needed_date} onChange={setC("needed_date")} data-testid="co-needed" /></div>
        </div>
        <div><label htmlFor="co-notes" className="block text-sm font-medium text-[#0b192c] mb-1.5">Additional Notes</label><textarea id="co-notes" className={inputCls} rows={2} value={cust.notes} onChange={setC("notes")} placeholder="Optional" data-testid="co-notes" /></div>
      </div>

      {/* Price breakdown */}
      {price && (
        <div className="mt-5 rounded-xl bg-[#f8f9fa] border border-slate-200 p-4 text-sm" data-testid="order-breakdown">
          {price.line_items.map((i, idx) => (<div key={idx} className="flex justify-between text-slate-600 py-0.5"><span>{i.name}</span><span>{money(i.cents)}</span></div>))}
          <div className="flex justify-between text-slate-600 py-0.5"><span>Card processing ({cfg?.processingFeePercent}%)</span><span>{money(price.processing_fee_cents)}</span></div>
          <div className="flex justify-between font-bold text-[#0b192c] mt-2 pt-2 border-t border-slate-200"><span>Total</span><span data-testid="order-total">{money(price.total_cents)}</span></div>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">Mandatory U.S. government fees shown above are set by and paid to the U.S. Department of State and are in addition to our service fee. UPV Services is a private courier, not a government agency. Processing times are estimates and are not guaranteed.</p>
        </div>
      )}

      {/* Payment */}
      {cfg?.enabled ? (
        <>
          <div className="mt-5">
            <span className="flex items-center gap-2 text-sm font-medium text-[#0b192c] mb-2"><CreditCard className="w-4 h-4 text-[#d90429]" aria-hidden="true" /> Pay by card</span>
            <div id="sq-card" className="rounded-xl border border-slate-200 p-1 min-h-[52px]" role="group" aria-label="Credit or debit card details" />
          </div>
          <button onClick={pay} disabled={loading || !selReady || !price} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#d90429] text-white px-6 py-4 font-semibold text-base hover:bg-[#ef233c] transition-colors disabled:opacity-60" data-testid="pay-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-4 h-4" /> Pay {price ? money(price.total_cents) : "securely"}</>}
          </button>

          {/* Digital wallets */}
          <div className={anyWallet ? "block" : "hidden"} data-testid="wallets-section">
            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">or pay with</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="space-y-2.5">
              {wallets.apple && (
                <button onClick={payApple} disabled={loading || !price} data-testid="apple-pay-btn"
                  className="w-full h-[48px] rounded-xl bg-black text-white font-semibold flex items-center justify-center gap-2 hover:bg-black/90 transition-colors disabled:opacity-60">
                  <Apple className="w-5 h-5" /> Pay
                </button>
              )}
              <div id="gpay-btn" className={wallets.google ? "" : "hidden"} data-testid="google-pay-btn" />
              <div id="cashapp-btn" className={wallets.cashapp ? "" : "hidden"} data-testid="cash-app-pay-btn" />
            </div>
          </div>

          {selReady && !price && <p className="mt-2 text-center text-xs text-slate-500">Fees for this service are confirmed by phone — please call {PHONE}.</p>}
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="w-3.5 h-3.5" /> Secure payment via Square.</p>
        </>
      ) : (
        <div className="mt-5 rounded-xl border border-slate-200 bg-[#f8f9fa] p-4 text-center" data-testid="checkout-disabled">
          <p className="text-sm text-slate-600">Online payment is being set up. Call us and we'll complete your order right away.</p>
          <a href={PHONE_HREF} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#0b192c] text-white px-5 py-2.5 font-semibold hover:bg-[#1a2a40] transition-colors"><Phone className="w-4 h-4 text-[#ef233c]" /> Call {PHONE}</a>
        </div>
      )}

      <a href={PHONE_HREF} className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-[#0b192c] hover:text-[#d90429] transition-colors" data-testid="need-help-call">
        <Phone className="w-4 h-4" /> Need Help? Call Us For A Quote — {PHONE}
      </a>
    </div>
  );
};
